<template>
  <v-dialog v-model="dialogVisible" max-width="500px" persistent>
    <v-card>
      <v-card-title class="headline"> Estender Período da Etapa </v-card-title>

      <v-card-text>
        <div class="mb-4">
          <p class="body-1 mb-2"><strong>Etapa:</strong> {{ etapaNome }}</p>
          <p class="body-2 text--secondary">
            Selecione a nova data final para estender o período desta etapa.
          </p>
        </div>
        <v-form ref="form" v-model="formValid">
          <v-menu
            v-model="datePickerVisible"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dataFormatada"
                label="Nova Data Final *"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="dataRules"
                :error-messages="errorMessages"
                outlined
                dense
              ></v-text-field>
            </template>

            <v-date-picker
              v-model="novaData"
              :min="dataMinima"
              @input="datePickerVisible = false"
              locale="pt-br"
              first-day-of-week="0"
            ></v-date-picker>
          </v-menu>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="dataExtensao"
          text
          @click="removerExtensao"
          color="red darken-3"
          :disabled="carregando"
        >
          <v-icon left>mdi mdi-clock-remove-outline</v-icon>
          Remover extensão
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="fecharModal" :disabled="carregando"> Cancelar </v-btn>
        <v-btn
          color="green darken-3"
          dark
          @click="estenderEtapa"
          :loading="carregando"
          :disabled="!formValid || !novaData"
        >
          <v-icon left>mdi-check</v-icon>
          Estender
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ModalEstenderEtapa",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    etapaId: {
      type: [String, Number],
      default: null,
    },
    etapaNome: {
      type: String,
      default: "",
    },
    dataExtensao: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      dialogVisible: false,
      datePickerVisible: false,
      formValid: false,
      carregando: false,
      novaData: this.dataExtensao,
      errorMessages: [],

      dataRules: [
        (v) => !!v || "Data é obrigatória",
        (v) => this.validarDataMinima(v) || "A data deve ser posterior à data atual",
      ],
    };
  },

  computed: {
    dataFormatada() {
      if (!this.novaData) return "";

      const [ano, mes, dia] = this.novaData.split("-");
      return `${dia}/${mes}/${ano}`;
    },

    dataMinima() {
      const hoje = new Date();
      const amanha = new Date(hoje.getTime() + 24 * 60 * 60 * 1000);
      return amanha.toISOString().split("T")[0];
    },
  },

  watch: {
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal;
        if (newVal) {
          this.resetarFormulario();
        }
      },
      immediate: true,
    },

    dialogVisible(newVal) {
      if (!newVal && this.visible) {
        this.$emit("fechar");
      }
    },

    dataExtensao(newVal) {
      this.novaData = newVal;
    },
  },

  methods: {
    validarDataMinima(data) {
      if (!data) return false;

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const [dia, mes, ano] = data.split("/");
      const dataSelecionada = new Date(ano, mes - 1, dia);

      return dataSelecionada > hoje;
    },

    resetarFormulario() {
      this.novaData = null;
      this.errorMessages = [];

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    fecharModal() {
      this.dialogVisible = false;
      this.$emit("fechar");
    },

    async estenderEtapa() {
      if (!this.formValid || !this.novaData || !this.etapaId) {
        return;
      }

      this.carregando = true;
      this.errorMessages = [];

      this.$modals.warn({
        title: "Cuidado!",
        message: `Tem certeza que deseja estender esta etapa <strong>${this.etapaNome}</strong>?`,
        confirmationCode: this.etapaId,
        confirmationMessage: `Por favor, digite <strong>${this.etapaId}</strong> para confirmar`,
        buttons: [
          {
            text: "Cancelar",
            callback: async () => {
              this.$modals.close();
              this.fecharModal();
            },
            props: { color: "warning", small: true },
          },
          {
            text: "Confirmar",
            disableIfCodeDoesntMatch: true,
            callback: async () => {
              this.$modals.close();
              this.carregando = true;
              try {
                await this.$services.etapasService.estenderEtapa(this.etapaId, this.novaData);

                this.$emit("sucesso", {
                  etapaId: this.etapaId,
                  novaData: this.novaData,
                });

                this.$toast.success("Etapa estendida com sucesso!");
              } catch (error) {
                this.$handleError(error);
              } finally {
                this.fecharModal();
                this.carregando = false;
              }
            },
            props: { color: "green", dark: true, small: true },
          },
        ],
      });
    },
    async removerExtensao() {
      if (!this.etapaId) return;

      this.$modals.danger({
        title: "Cuidado!",
        message: `Tem certeza que deseja remover a extensão da etapa <strong>${this.etapaNome}</strong>?`,
        confirmationCode: this.etapaId,
        confirmationMessage: `Por favor, digite <strong>${this.etapaId}</strong> para confirmar`,
        buttons: [
          {
            text: "Cancelar",
            callback: () => this.$modals.close(),
            props: { color: "warning", small: true },
          },
          {
            text: "Confirmar",
            disableIfCodeDoesntMatch: true,
            callback: async () => {
              this.$modals.close();
              this.carregando = true;
              try {
                await this.$services.etapasService.estenderEtapa(this.etapaId, null);
                this.$emit("sucesso", { etapaId: this.etapaId, novaData: null });
                this.$toast.success("Extensão removida com sucesso!");
                this.fecharModal();
              } catch (error) {
                this.$handleError(error);
              } finally {
                this.carregando = false;
              }
            },
            props: { color: "error", small: true },
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.headline {
  padding-bottom: 16px !important;
}

.v-card__text {
  padding-top: 0 !important;
}
</style>
